
const Util = require('util');

const libPictSectionRecordSet = require('pict-section-recordset');

const libPictSectionForm = require('pict-section-form');

const libPictPreprocessor = require('pict-template-preprocessor');

const libHeadlightConfigurableApplication = require('headlight-configurable-application');

// Providers
const libLIMSRouter = require('./providers/LIMS-Router.js');

const libEntityProvider = require('./providers/Entity-Provider.js');

const HeadlightDynamicInputsBundle = require('headlight-dynamic-inputs-bundle');
const libVocabularySelectorMap = require('headlight-dynamic-inputs-bundle/source/providers/_VocabularySelectorMap.js');

// Views
const configEmptyView = require('./views/Empty.json');
const LIMSPrinterCodes = require('./views/Printer-Codes.js');
const LIMSTestbed = require('./views/LIMS-Testbed.js');
const LIMSLocationRecordView = require('./views/Location-Record-View.js');
const LIMSSampleRecordCreate = require('./views/Sample-Record-Create.js');
const LIMSLocationRecordCreate = require('./views/Location-Record-Create.js');
const LIMSSampleRecordEdit = require('./views/Sample-Record-Edit.js');
const LIMSLocationRecordEdit = require('./views/Location-Record-Edit.js');
const LIMSSampleRecordView = require('./views/Sample-Record-View.js');
const LIMSDocumentRecordEdit = require('./views/Document-Record-Edit.js');
const LIMSDocumentRecordView = require('./views/Document-Record-View.js');
const LIMSDocumentRecordCreate = require('./views/Document-Record-Create.js');
const LIMSSamplePlanCopy = require('./views/SamplePlan-Copy.js');


const libDynamicRecordsetSolver = require('pict-section-recordset/source/providers/RecordSet-DynamicRecordsetSolver.js');
const libFilterDataProvider = require('pict-section-recordset/source/providers/Filter-Data-Provider.js');
const libFilterPersistenceView = require('pict-section-recordset/source/views/Filter-PersistenceView.js');

const customLoginPageOptions = {
	title: 'HeadLight LIMS',
	titleStyles: `
		z-index: 1;
		text-shadow:  5px 9px 2px var(--bulma-black);
		word-wrap: break-word;
		overflow-wrap: break-word;
		text-align: center;
		font-size: 4rem !important; /* Default size */
		margin: 0 0 5vh 0;	/* Default margin */`,
	wallpaperStyles: `
		background-image: url('../../images/headlight-samples.jpg');
		background-size: cover;
		background-repeat: no-repeat;
		background-color: var(--bulma-grey);
		background-blend-mode: multiply;`,
	closePanelsOnLogout: true,
};

/**
 * @typedef {Object} Anticipate
 * @property {(fNext: (error?: any) => any) => void} anticipate - Add a function to the serial async queue.
 * @property {() => ((error?: any) => any)} buildAnticipatorCallback - Don't know.
 * @property {(fCallback: (error?: any) => any) => void} wait - Wait for the serial async queue to complete and return the final result.
 */

/**
 * FIXME: figure out how to flow these types through in a more sane way....
 * @typedef {import('headlight-configurable-application').Pict &
 * {
 *   HeadlightRestClient: import('headlight-rest-client'),
 *   ManifestPackagingService: import('manifest-packaging-service'),
 *   providers:
 *   {
 *		  RecordExtension: import('./providers/RecordExtension-Provider.js'),
 *		  FormInitialBundle: InstanceType<typeof import('./providers/FormInitialBundle-Provider.js')>,
 *   },
 *   MetaTemplate: any, // import('fable').MetaTemplate,
 *   PictApplication: LIMS,
 *   PictSectionRecordSet: InstanceType<libPictSectionRecordSet>,
 *   PictRouter: import('pict-router'),
 *   fable: import('fable') & import('pict'),
 *   newAnticipate: () => Anticipate,
 *   log: any,
 *   newManyfest: (manifest: any) => any
 *   options: any,
 * 	 RestClient: any,
 *   instantiateServiceProviderWithoutRegistration: (hash: string) => any,
 *   instantiateServiceProviderIfNotExists: (hash: string) => any,
 *   TransactionTracking: import('pict/types/source/services/Fable-Service-TransactionTracking'),
 * }} LIMSPict
 */


class LIMS extends libHeadlightConfigurableApplication
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {string} */
		this.cachedLogoutRoute = null;
		/** @type {LIMSPict} */
		this.pict;

		this.navInitialized = false;

		this.rnis = [];
		/** @type {Promise<Array<any>>|null} */
		this._reportNamedInstanceLoad = null;
		/** @type {number|null} */
		this._reportNamedInstanceCustomer = null;

		// Register and instantiate -- wrappers install automatically
		this.pict.addServiceType('PictTemplatePreprocessor', libPictPreprocessor);
		this.pict.instantiateServiceProvider('PictTemplatePreprocessor');

		// Add the pict form service
		this.pict.addServiceType('PictSectionForm', libPictSectionForm);
		// for filter data/persistence management
		this.pict.addProvider('FilterDataProvider', libFilterDataProvider.default_configuration, libFilterDataProvider);
		this.pict.addView('FilterPersistenceView', libFilterPersistenceView.default_configuration, libFilterPersistenceView);

		this.pict.addAndInstantiateServiceType('ManifestPackagingService', require('manifest-packaging-service'));
		this.pict.addServiceType('PictSectionRecordSet', libPictSectionRecordSet);
		this.dynamicInputsBundle = new HeadlightDynamicInputsBundle(this.pict);
		this.dynamicInputsBundle.attachProviders();
		this.pict.addProvider('EntityDocumentProvider', { Entity: 'Document' }, libEntityProvider);
		this.pict.addProvider('ModulesProvider', { }, require('../../providers/ModulesProvider.js'));
		this.pict.addProvider('MarkdownProvider', { }, require('../../providers/MarkdownProvider.js'));
		this.pict.addProvider('ComplexFilterProvider',
		{
			endCapTemplate: '<span class="is-hidden-mobile">{~CreateEntityButton:Record~}</span>',
		},
		require('../../providers/ComplexFilterProvider.js'));
		this.pict.addProvider('ImprovedTabularRecordSetProvider',
		{
			endCapTemplate: '<span class="is-hidden-desktop is-hidden-tablet">{~CreateEntityButton:Record~}</span>',
			includePrint: true,
			overrideURLTemplate: `#/{~D:Record.Payload.Payload.RecordSet~}/{~DVBK:Record.Payload.Data:Record.Payload.Payload.GUIDAddress~}`,
		},
		require('../../providers/ImprovedTabularRecordSetProvider.js'));
		this.pict.addProvider('RecordExtension', { }, require('./providers/RecordExtension-Provider.js'));
		this.pict.addProvider('FormInitialBundle', { }, require('./providers/FormInitialBundle-Provider.js'));
		this.pict.addProvider('EntityCacheConfigurationProvider', { }, require('../../providers/EntityCacheConfigurationProvider.js'));

		this.AppData.Interactive = true;

		this.AppData.listConfigurations = {};

		this.AppData.SampleStatuses = [];

		this.AppData.disableSampleCreate = false;
		this.AppData.disableSampleEdit = false;
		this.PermissionsControl = new window.PermissionsControl({ CacheNamespace: 'LIMS', CacheExpirationMinutes: 2 });
		this.PermissionsControl.clear();

		// Application usability views
		this.pict.addView('Empty', configEmptyView);
		this.pict.addView('Jumbotron', require('./views/Jumbotron.js').default_configuration, require('./views/Jumbotron.js'));
		this.pict.addView('LIMSPrinterCodes', LIMSPrinterCodes.default_configuration, LIMSPrinterCodes);
		this.pict.addView('LIMSTestbed', LIMSTestbed.default_configuration, LIMSTestbed);
		this.pict.addView('LIMSLocationRecordView', LIMSLocationRecordView.default_configuration, LIMSLocationRecordView);
		this.pict.addView('LIMSLocationRecordEdit', LIMSLocationRecordEdit.default_configuration, LIMSLocationRecordEdit);
		this.pict.addView('LIMSLocationRecordCreate', LIMSLocationRecordCreate.default_configuration, LIMSLocationRecordCreate);
		this.pict.addView('LIMSDocumentRecordView', LIMSDocumentRecordView.default_configuration, LIMSDocumentRecordView);
		this.pict.addView('LIMSDocumentRecordEdit', LIMSDocumentRecordEdit.default_configuration, LIMSDocumentRecordEdit);
		this.pict.addView('LIMSDocumentRecordCreate', LIMSDocumentRecordCreate.default_configuration, LIMSDocumentRecordCreate);
		this.pict.addView('LIMSSampleRecordCreate', LIMSSampleRecordCreate.default_configuration, LIMSSampleRecordCreate);
		this.pict.addView('LIMSSampleRecordEdit', LIMSSampleRecordEdit.default_configuration, LIMSSampleRecordEdit);
		this.pict.addView('LIMSSampleRecordView', LIMSSampleRecordView.default_configuration, LIMSSampleRecordView);
		this.pict.addView('LIMSSamplePlanCopy', LIMSSamplePlanCopy.default_configuration, LIMSSamplePlanCopy);
		this.pict.addServiceType('SamplePlanService', require('headlight-lims-sampleplan'));

		this.sharedBundleConfig =
		[
			{
				'Entity': 'ListDefault',
				'Filter': 'FBV~NamespaceKey~EQ~Misc~FBV~NamespaceValue~EQ~Container',
				'Destination': 'AppData.Containers',
			},
			{
				'Entity': 'ListDefault',
				'Filter': 'FBV~NamespaceKey~EQ~Geographical~FBV~NamespaceValue~EQ~Country',
				'Destination': 'AppData.SourceCountries',
			},
			{
				'Entity': 'ListDefault',
				'Filter': 'FBV~NamespaceKey~EQ~Geographical~FBV~NamespaceValue~EQ~State',
				'Destination': 'AppData.SourceStates',
			},
			{
				'Entity': 'Location',
				'Filter': 'FBV~IDLocation~GT~0',
				'Destination': 'AppData.Locations',
			},
		];

		this.dynamicInputsBundle.attachInstructions();

		this.pict.MetaTemplate.addPattern('{~ParseSourceLocation:', '~}', (pHash, pData, fCallback) =>
		{
			const record = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			return this.formatLocation(record.Extension.SourceCity, record.Extension.SourceState, record.Extension.SourceCountry);
		});
		this.pict.MetaTemplate.addPattern('{~DateFormatLIMS:', '~}', (pHash, pData, fCallback) =>
		{
			// format the passed date or fallback to CreateDate
			const [ tmpDataAddress, tmpDateFormat ] = pHash.split('^');
			const tmpDate = this.pict.resolveStateFromAddress(tmpDataAddress, pData, fCallback) ||
				this.pict.resolveStateFromAddress(tmpDataAddress.split('.').slice(0, -1).join('.') + '.CreateDate', pData, fCallback);
			return tmpDate ? this.pict.fable.Dates.dayJS(tmpDate).format(tmpDateFormat) : '';
		});
		this.pict.MetaTemplate.addPattern('{~DateFormatNoFallback:', '~}', (pHash, pData, fCallback) =>
		{
			// format the passed date or fallback to CreateDate
			const [ tmpDataAddress, tmpDateFormat ] = pHash.split('^');
			const tmpDate = this.pict.resolveStateFromAddress(tmpDataAddress, pData, fCallback);
			return tmpDate && this.pict.fable.Dates.dayJS(tmpDate).isValid() ? this.pict.parseTemplate(`{~DateFormat:${tmpDataAddress}^${tmpDateFormat}~}`, pData) : '';
		});
		this.pict.MetaTemplate.addPattern('{~DocumentTypeName:', '~}', (pHash, pData, fCallback) =>
		{
			const hash = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			let type = '';
			for (const r of this.rnis)
			{
				if (r?.Hash === hash)
				{
					type = r.Name;
				}
			}
			return type;
		});
		this.pict.MetaTemplate.addPattern('{~SampleStatusName:', '~}', (pHash, pData, fCallback) =>
		{
			return this.resolveSampleStatusName(this.pict.resolveStateFromAddress(pHash, pData, fCallback));
		});
		this.pict.MetaTemplate.addPattern('{~CreateEntityButton:', '~}', (pHash, pData, fCallback) =>
		{
			const supportedEntities = [ 'Location', 'Sample' ];
			let createSupported = false;
			let entity = '';
			for (const e of supportedEntities)
			{
				if (window.location.href.includes(`${ e }/List`))
				{
					createSupported = true;
					entity = e;
				}
			}
			if (entity == 'Sample' && this.AppData.disableSampleCreate)
			{
				createSupported = false;
			}
			return createSupported ? /*html*/`
				<div class="column has-text-right is-narrow pl-0">
					<a href="#/Create-${ entity }" class="button is-info">
						<span class="icon">
							<i class="fas fa-plus"></i>
						</span>
						<span class="icon-text">Create New ${ entity }</span>
					</a>
				</div>
			` : '';
		});
		this.pict.MetaTemplate.addPattern('{~SampleClasses:', '~}', (pHash, pData, fCallback) =>
		{
			return window.location.href.includes('/Sample/List') ? 'has-6-cols' : 'has-5-cols';
		});
		this.pict.TemplateProvider.addTemplate('User-Name', /*html*/`
			{~D:Record.NameFirst~} {~D:Record.NameLast~}
		`);
		this.pict.TemplateProvider.addTemplate('Entity-Name', /*html*/`
			{~D:Record.Name~}
		`);
		this.pict.addProviderSingleton('DynamicRecordsetSolver', libDynamicRecordsetSolver.default_configuration, libDynamicRecordsetSolver);
	}

	formatLocation(city, state, country)
	{
		if (!city && !state && !country)
		{
			return '';
		}
		else if (city && state)
		{
			return `${ city }, ${ state }`;
		}
		else if ((city && country) || (state && country))
		{
			return `${ city || '---' }, ${ state || '---' }, ${ country || '---' }`;
		}
		else
		{
			return city || state || country;
		}
	}

	async onBeforeRenderAsync(fCallback)
	{
		this.pict.log.info('LIMS onBeforeRenderAsync');
		if (this.loggedIn)
		{
			await this.loadReportNamedInstances();
		}
		return super.onBeforeRenderAsync(fCallback);
	}

	/**
	 * The customer's ReportNamedInstances, loaded once per customer.
	 *
	 * Both the app render hook and the post-login setup want these, and the read behind them
	 * (DocumentProvider.getReportNamedInstancesByCustomer) is an uncached paged entity-set fetch, so
	 * they used to each hit the wire at startup. They now share one in-flight promise. A failed load
	 * clears the memo so the next ask retries.
	 *
	 * @return {Promise<Array<any>>} - The customer's ReportNamedInstance records.
	 */
	async loadReportNamedInstances()
	{
		const tmpIDCustomer = this.AppData.CurrentUser?.CustomerID;
		if (!this._reportNamedInstanceLoad || (this._reportNamedInstanceCustomer !== tmpIDCustomer))
		{
			this._reportNamedInstanceCustomer = tmpIDCustomer;
			this._reportNamedInstanceLoad = this.pict.providers.DocumentProvider.getReportNamedInstancesByCustomer(tmpIDCustomer)
				.then((pReportNamedInstances) =>
				{
					this.rnis = pReportNamedInstances;
					return pReportNamedInstances;
				})
				.catch((pError) =>
				{
					this._reportNamedInstanceLoad = null;
					throw pError;
				});
		}
		return this._reportNamedInstanceLoad;
	}

	/**
	 * The customer's curated SampleStatus vocabulary — a small code list, read in full, used to label
	 * the Sample list's Status column. Mirrors the read the SampleStatusSelector input type does
	 * (meadow pluralizes a scope by appending an 's', hence `SampleStatuss`). A customer that has
	 * curated no rows resolves to an empty list, and the default labels are used instead.
	 *
	 * @return {Promise<void>}
	 */
	async loadSampleStatusVocabulary()
	{
		this.AppData.SampleStatuses = await new Promise((resolve) =>
		{
			this.pict.HeadlightRestClient.getJSON('SampleStatuss/FilteredTo/FSF~Ordinal~ASC~0/0/500', (pError, pRecords) =>
			{
				if (pError || !Array.isArray(pRecords))
				{
					this.pict.log.warn(`Could not load the SampleStatus vocabulary; falling back to the default status labels: ${pError?.message || pError || 'unexpected payload'}`);
					return resolve([]);
				}
				resolve(pRecords);
			});
		});
	}

	/**
	 * Sample.Status persists the SampleStatus code ('RECV'), not a display name, so anywhere the raw
	 * column is rendered it reads as a hash. Resolve it against the customer's curated vocabulary,
	 * falling back to the same default list the SampleStatusSelector input type uses, and finally to
	 * the raw code for a status nobody has a label for.
	 *
	 * @param {string} pStatus - The status code stored on the Sample record.
	 * @return {string} - The display name.
	 */
	resolveSampleStatusName(pStatus)
	{
		if (!pStatus)
		{
			return '';
		}
		for (const tmpStatus of this.AppData.SampleStatuses || [])
		{
			if (tmpStatus?.Hash === pStatus)
			{
				return tmpStatus.Name || pStatus;
			}
		}
		for (const tmpFallbackStatus of libVocabularySelectorMap.DEFAULT_SAMPLE_STATUS_SELECT_OPTIONS)
		{
			if (tmpFallbackStatus.id === pStatus)
			{
				return tmpFallbackStatus.text;
			}
		}
		return pStatus;
	}

	/**
	 * Force the record set list/dashboard cells that need an app-level renderer onto the right
	 * template, whatever composed them.
	 *
	 * A column with no explicit ValueTemplate falls to the generic ProcessCell renderer, which only
	 * resolves ID* foreign keys and otherwise prints the raw column value — so Sample.Status shows as
	 * `RECV`. The in-repo list manifest declares the right template for its own Status descriptor, but
	 * the column chooser's schema tier and any database manifest that supersedes it do not, so the
	 * composed cells get rewritten here instead.
	 *
	 * onBeforeRenderList is the recordset section's host seam: it runs after the cells are composed and
	 * again on every repaint (a chooser toggle, a page change), and the cells it is handed are per-paint
	 * shallow copies, so mutating them never bleeds into the shared manifest.
	 */
	overrideRecordSetValueTemplates()
	{
		const tmpValueTemplateOverrides =
		{
			Sample: { Status: '{~SampleStatusName:Record.Payload.Status~}' },
		};
		const fApplyValueTemplateOverrides = (pRecordListData) =>
		{
			const tmpOverrides = tmpValueTemplateOverrides[pRecordListData?.RecordSetConfiguration?.Entity];
			if (!tmpOverrides || !Array.isArray(pRecordListData.TableCells))
			{
				return pRecordListData;
			}
			for (const tmpCell of pRecordListData.TableCells)
			{
				if (tmpCell && (tmpCell.Key in tmpOverrides))
				{
					// New object rather than an in-place edit: PictDashboard is still the manifest's own.
					tmpCell.PictDashboard = Object.assign({}, tmpCell.PictDashboard, { ValueTemplate: tmpOverrides[tmpCell.Key] });
				}
			}
			return pRecordListData;
		};
		for (const tmpView of [ this.pict.PictSectionRecordSet?.childViews?.list, this.pict.PictSectionRecordSet?.childViews?.dashboard ])
		{
			if (!tmpView || tmpView._limsValueTemplateOverridesInstalled)
			{
				continue;
			}
			const fBaseOnBeforeRenderList = tmpView.onBeforeRenderList.bind(tmpView);
			tmpView.onBeforeRenderList = (pRecordListData) => fApplyValueTemplateOverrides(fBaseOnBeforeRenderList(pRecordListData));
			tmpView._limsValueTemplateOverridesInstalled = true;
		}
	}

	/**
	 * This will change later but for now this can be called when needed to set the columns to display on the record set list view for Samples.
	 */
	async setSampleListColumns()
	{
		if (!this.loggedIn)
		{
			return;
		}
		// TODO: this is dumb, we need to genericize display names of stuff somewhere.
		const sampleColumnDisplayNameLookup =
		{
			'IDSample': 'Sample ID',
			'GUIDSample': 'Sample GUID',
			'CreateDate': 'Create Date',
			'CreatingIDUser': 'Logged By',
			'UpdateDate': 'Update Date',
			'UpdatingIDUser': 'Updating User',
			'DesignatedTime': 'Sampled On',
			'Deleted': 'Deleted',
			'DeleteDate': 'Delete Date',
			'DeletingIDUser': 'Deleting User',
			'ExternalSyncDate': 'External Sync Date',
			'IDCustomer': 'Customer',
			'QRCode': 'QR Code',
			'CaptureLatitude': 'Capture Latitude',
			'CaptureLongitude': 'Capture Longitude',
			'Name': 'Name',
			'IDProject': 'Project',
			'ParentIDSample': 'Parent Sample',
			'IDMaterial': 'Material',
			'IDProductionPlant': 'Production Plant',
			'IDLineItem': 'Line Item',
			'Notes': 'Notes',
			'Status': 'Status',
			'LabControlNumber': 'PSID', //NOTE: paragon specific
			'LabReferenceNumber': 'Lab Reference Number',
			'ProductName': 'Product Name',
			'MaterialType': 'Material Type',
			'IDOrganization': 'Producer',
			'IDMixDesign': 'Mix Design',
			'ManufacturerCode': 'Manufacturer Code',
			'IntendedUse': 'Intended Use',
			'BuyUSA': 'Buy USA',
			'ReceivedFrom': 'Source',
			'GeographicalArea': 'Geographical Area',
			'Station': 'Station',
			'Offset': 'Offset',
			'RepresentedQuantity': 'Represented',
			'SampledQuantity': 'Sampled Quantity',
			'SampledQuantityUnits': 'Sampled Quantity Units',
			'RepresentedQuantityUnits': 'Represented Quantity Units',
			'Units': 'Units',
			'RequestedBy': 'Requested By',
			'ExternalSyncGUID': 'External Sync GUID',
			'IDProduct': 'Product',
			'SampledByIDUser': 'Sampled By',
			'WitnessIDUser': 'Witness',
			'AuthorizedIDUser': 'Authorized By',
			'CMSIDUser': 'CMS User',
			'Datum': 'Datum',
		};
		await this.pict.providers.FieldSettingsProvider.registerNamespaceByEntity('SampleList', 'Sample', 'List', false, [ 'IDMaterial' ]);
		// Update the shown colums based on config.
		if (this.pict?.PictSectionRecordSet?.recordSetProviderConfigurations?.Sample)
		{
			this.pict.PictSectionRecordSet.recordSetProviderConfigurations.Sample.RecordSetListColumns = [];
			for (const f of this.pict.providers.FieldSettingsProvider.getShownFields('SampleList'))
			{
				this.pict.PictSectionRecordSet.recordSetProviderConfigurations.Sample.RecordSetListColumns.push(
					{
						Key: f,
						DisplayName: sampleColumnDisplayNameLookup[f],
					},
				);
			}
		}
	}

	/**
	 * This will change later but for now this can be called when needed to set the columns to display on the record set list view for Locations.
	 */
	async setLocationListColumns()
	{
		if (!this.loggedIn)
		{
			return;
		}
		// TODO: this is dumb, we need to genericize display names of stuff somewhere.
		const locationColumnDisplayNameLookup =
		{
			'IDLocation': 'Location ID',
			'GUIDLocation': 'Location GUID',
			'CreateDate': 'Create Date',
			'CreatingIDUser': 'Creating User',
			'UpdateDate': 'Update Date',
			'UpdatingIDUser': 'Updating User',
			'Deleted': 'Deleted',
			'DeleteDate': 'Delete Date',
			'DeletingIDUser': 'Deleting User',
			'ExternalSyncDate': 'External Sync Date',
			'Type': 'Type',
			'Name': 'Name',
			'Description': 'Description',
			'QRCode': 'QR Code',
			'Address': 'Address',
			'ExternalLocationID': 'External Location',
			'ParentIDLocation': 'Parent Location',
			'City': 'City',
			'State': 'State',
			'Zip': 'Zip Code',
			'Phone': 'Phone Number',
			'Latitude': 'Latitude',
			'Longitude': 'Longitude',
			'IDCustomer': 'Customer',
		};
		await this.pict.providers.FieldSettingsProvider.registerNamespaceByEntity('LocationList', 'Location', 'List', false, [ 'Name' ]);
		// Update the shown colums based on config.
		if (this.pict?.PictSectionRecordSet?.recordSetProviderConfigurations?.Location)
		{
			this.pict.PictSectionRecordSet.recordSetProviderConfigurations.Location.RecordSetListColumns = [];
			for (const f of this.pict.providers.FieldSettingsProvider.getShownFields('LocationList'))
			{
				this.pict.PictSectionRecordSet.recordSetProviderConfigurations.Location.RecordSetListColumns.push(
					{
						Key: f,
						DisplayName: locationColumnDisplayNameLookup[f],
					},
				);
			}
		}
	}

	async logout(event)
	{
		// I think this should be a consistent method on the base class.
		this.pict.views.Empty.renderAsync(
			(pError) =>
			{
				super.logout(event);
			});
	}

	/**
	 * Show the jumbotron view - if the first time, render it, else just show it
	 */
	showJumbotron()
	{
		const tempJumbotronContainer = document.getElementById('Jumbotron');

		if (!tempJumbotronContainer)
		{
			this.pict.views.Jumbotron.renderAsync();
		}
		else
		{
			tempJumbotronContainer.classList.remove('is-hidden');
		}
	}

	/**
	 * Hide the jumbotron view
	 */
	hideJumbotron()
	{
		const tempJumbotronContainer = document.getElementById('Jumbotron');
		if (tempJumbotronContainer)
		{
			tempJumbotronContainer.classList.add('is-hidden');
		}
	}

	/**
	 * Paint a skeleton into a detail surface before its data lands, so the page doesn't sit as an
	 * empty frame and then pop. Every detail view renders with 'replace' into its destination, so
	 * the real render overwrites this with no teardown.
	 *
	 * @param {string} [pDestinationAddress] - Where to paint; defaults to the app container.
	 * @param {number} [pRowCount] - How many ghost field rows to draw.
	 * @param {boolean} [pIncludeHeader] - Draw the record header + tab strip placeholders (full-page shells only).
	 */
	showDetailLoadingShell(pDestinationAddress, pRowCount, pIncludeHeader)
	{
		try
		{
			const tmpDestination = pDestinationAddress || '#LIMS-Application-Container';
			const tmpRowCount = Math.max(1, Math.min(24, pRowCount || 6));
			const tmpIncludeHeader = (pIncludeHeader !== false);
			let tmpRows = '';
			for (let i = 0; i < tmpRowCount; i++)
			{
				tmpRows += '<div class="lims-detail-skeleton-row"></div>';
			}
			this.pict.ContentAssignment.assignContent(tmpDestination, /*html*/`
				<div class="lims-detail-loading">
					<div class="lims-detail-loading-inner">
						<span class="lims-detail-spinner" aria-hidden="true"><i class="fas fa-fw fa-circle-notch"></i></span>
						<span>Loading…</span>
					</div>
					<div class="lims-detail-skeleton" aria-hidden="true">
						${ tmpIncludeHeader ? '<div class="lims-detail-skeleton-header"></div><div class="lims-detail-skeleton-tabs"></div>' : '' }
						${ tmpRows }
						<div class="lims-detail-skeleton-fade"></div>
					</div>
				</div>`);
		}
		catch (error)
		{
			// The shell is purely cosmetic; never let it break a navigation.
			this.pict.log.warn(`Error rendering the LIMS detail loading shell: ${error.message || error}`);
		}
	}

	showProfile()
	{
		this.pict.views.HeadlightPanels.handlePanelToggle('Profile', 'open');
	}

	_updateUserProfile()
	{
		const session = this.AppData.CurrentUser;
		if (session?.LoggedIn)
		{
			let userHash = session.LoginID;
			if (!userHash)
			{
				userHash = session.Email;
			}
			if (!userHash)
			{
				userHash = `${session.NameFirst} ${session.NameLast}`;
			}
			if (!userHash)
			{
				userHash = 'no user name';
			}
			const userColor = this.pict.providers.UtilityProvider.stringToHSLColor(userHash, 75, 50);
			$('#Header_ProfileIcon').css('color', userColor);
			const name = $('#Header_ProfileName');
			name.text(`${session.NameFirst.charAt(0)}${session.NameLast.charAt(0)}`);
		}
		else
		{
			$('#Header_ProfileIcon').css('color', null); //FIXME: ??
			$('#Header_ProfileName').text('Profile');
		}
	}

	updateProgramDisplayInNav()
	{
		const session = this.AppData.CurrentUser;
		if (session?.LoggedIn)
		{
			// get elements
			const programImage = document.getElementById('Header_ProgramImage');
			const programName = document.getElementById('Header_ProgramName');

			if (!programImage || !programName || !(programImage instanceof HTMLImageElement))
			{
				this.pict.log.error('Could not find program image or name elements, or they are not of the expected type');
				return;
			}
			//check if program name exists
			if (this.AppData.Module?.ProgramName)
			{
				programName.textContent = this.AppData.Module.ProgramName;
			}
			//check if program image exists
			if (this.AppData.Module?.ProgramLogoURL)
			{
				programImage.src = this.AppData.Module.ProgramLogoURL;
			}
		}
	}


	/**
	 * Called during initialization to register templates.
	 */
	registerTemplates()
	{
		super.registerTemplates();
		const themeTemplateSets = this.customizeThemeTemplates(HeadlightDynamicInputsBundle.getThemeMap());
		for (const templateSet of Object.values(themeTemplateSets || { }))
		{
			this.pict.providers.PictFormSectionDefaultTemplateProvider.injectTemplateSet(templateSet);
		}
	}

	/**
	 * Change the theme to one of the available themes.
	 *
	 * @param {string} pTheme - The theme to change to.
	 */
	changeTheme(pTheme)
	{
		if (pTheme == this.theme)
		{
			return;
		}
		this.theme = pTheme;
		//FIXME: tried using print theme for read, but it is interactive ? ? ?
		const newTheme = 'PictProvider-Theme-' + (pTheme == 'Create' ? 'Edit' : pTheme == 'Read' ? 'Read' : pTheme);
		this.pict.log.info(`Changing theme to: ${newTheme}`);
		this.pict.views.PictFormMetacontroller.formTemplatePrefix = newTheme;
	}

	/**
	 * @typedef {Object} ThemeProvider
	 * @property {String} TemplatePrefix - The template prefix for the theme.
	 * @property {Array<Object>} Templates - The templates for the theme.
	 * TODO: this is probably incomplete, and definitely not living in the right place; move this eventually
	 */
	/**
	 * @typedef {{ [key: string]: ThemeProvider }} ThemeProviderMap
	 */
	/**
	 * Hook to customize theme providers.
	 *
	 * @param {ThemeProviderMap} templateSets - The template sets to customize.
	 * @return {ThemeProviderMap} The customized template sets.
	 */
	customizeThemeTemplates(templateSets)
	{
		return templateSets;
	}

	async onInitializeAsync(fCallback)
	{
		this.pict.views.PictFormMetacontroller.viewMarshalDestination = 'AppData';
		this.pict.instantiateServiceProvider('PictSectionRecordSet', this.options);
		this.pict.PictSectionRecordSet.sessionProviders.push(async (capability) =>
		{
			return this.pict.providers.SessionProvider.getSession().then((session) => session.LoggedIn).catch((error) => false);
		});
		this.pict.providers.SessionProvider.onSessionChange((pNewSession) =>
		{
			if (this.pict.views.HeadlightPanels)
			{
				this.pict.views.HeadlightPanels.closeAllPanels();
			}
		});
		//NOTE: this attaches listeners to the router when constructed, so we must do that before initializing the PSRS below, or we won't get hooks for its routes
		this.pict.addProvider('LIMSRouter', libLIMSRouter.default_configuration, libLIMSRouter);
		// Initialize the views for the foundation metacontroller service
		this.pict.PictSectionRecordSet.initialize();
		await super.onInitializeAsync(fCallback);
	}

	async onAfterInitializeAsync(fCallback)
	{
		try
		{
			this.pict.providers.ComplexFilterProvider.prepareComplexFilter();
			this.pict.providers.ImprovedTabularRecordSetProvider.prepareTable();
			this.overrideRecordSetValueTemplates();
			this.pict.PictSectionRecordSet.childViews.read.options.DefaultDestinationAddress = '#LIMS-Application-Container';
			this.pict.PictSectionRecordSet.childViews.list.options.DefaultDestinationAddress = '#LIMS-Application-Container';
			this.pict.PictSectionRecordSet.childViews.dashboard.options.DefaultDestinationAddress = '#LIMS-Application-Container';

			// set an event listener on the body for the escape to close any open modals
			document.body.addEventListener('keydown', (event) =>
			{
				if (event.key === 'Escape')
				{
					document.querySelectorAll('.modal').forEach((modal) =>
					{
						modal.classList.remove('is-active');
					});
				}
			});

			this._updateUserProfile();
			this.updateProgramDisplayInNav();
			const session = await this.pict.providers.SessionProvider.getSession();

			if (session.LoggedIn)
			{
				this.pict.options.Timezone = session.Timezone || 'America/Los_Angeles'; //TODO: are there cases where we need to do project level customization here?
				await this.loadSharedBundle();
			}
			this.pict.providers.QRCodeProvider.registerGlobalHandler((e) =>
			{
				console.log('[LIMS-Application-Web] Received QREvent: ', e);
				if (e.event === 'scanned-Sample' && e.payload?.GUIDSample)
				{
					window.open(`#/Sample/${ e.payload.GUIDSample }`, '_self');
				}
				if (e.event === 'open' && e.payload.length && e.payload[0] === 'cart')
				{
					this.pict.views.QRPanel.open();
				}
			});
			this.pict.providers.LIMSRouter.initialize();
			this.pict.providers.RecordSetRouter.initialize();
		}
		catch (error)
		{
			this.pict.log.error(`Error initializing LIMS: ${error.message || error}`);
		}
		finally
		{
			this.pict.views.PictFormMetacontroller.generateMetatemplate();
			super.onAfterInitializeAsync(fCallback);
		}
	}

	onAfterLoginAsync(fCallback)
	{
		super.onAfterLoginAsync(async (pError) =>
		{
			try
			{
				if (!this.loggedIn)
				{
					// set the login page background image
					this.pict.views.HeadlightLogin.customizeLoginPage(customLoginPageOptions);
				}
				else
				{
					//FIXME: need to fix the init flow so we aren't duplicating this in two places
					//FIXME: this does not exist...
					//this.AppData.Module = await this.pict.providers.ModulesProvider.getModuleByHash('LIMS_MANAGEMENT');
					this.pict.providers.RecordSetRouter.addRoutes(this.pict.providers.RecordSetRouter.pictRouter);
				}
			}
			catch (error)
			{
				this.pict.log.error(`Error during onAfterLoginAsync: ${error.message || error}`);
			}
			finally
			{
				fCallback(pError);
			}
		});
	}

	async loadSharedBundle()
	{
		await Util.promisify(this.pict.EntityProvider.gatherDataFromServer).bind(this.pict.EntityProvider)(this.sharedBundleConfig);
		this.seedReferenceEntityCache();
	}

	/**
	 * Push the shared bundle's already-loaded reference records into the entity record cache.
	 *
	 * Without this the Samples list re-reads storage locations a row at a time: the recordset's
	 * connected-entity prefetch only batches top-level ID* columns, so the list manifest's nested
	 * `{~E:Location^Record.Payload.Extension.StorageLocation^…~}` lookup falls through to a per-row
	 * EntityProvider.getEntity, which has no in-flight de-duplication. The whole Location set is
	 * already in hand from login, so it just needs to land in the bucket those lookups read.
	 *
	 * The bucket's 30 second default lifetime is also far too short for a set loaded once per
	 * session, hence the per-seed MaxAge.
	 */
	seedReferenceEntityCache()
	{
		const tmpSeeds =
		[
			{ Entity: 'Location', Records: this.AppData.Locations, IDField: 'IDLocation', MaxAge: 15 * 60 * 1000 },
		];
		for (const tmpSeed of tmpSeeds)
		{
			if (!Array.isArray(tmpSeed.Records) || !tmpSeed.Records.length)
			{
				continue;
			}
			try
			{
				this.pict.EntityProvider.initializeCache(tmpSeed.Entity);
				const tmpCache = this.pict.EntityProvider.recordCache[tmpSeed.Entity];
				tmpCache.maxAge = tmpSeed.MaxAge;
				tmpCache.maxLength = Math.max(tmpCache.maxLength, tmpSeed.Records.length);
				for (const tmpRecord of tmpSeed.Records)
				{
					if (tmpRecord && tmpRecord[tmpSeed.IDField])
					{
						tmpCache.put(tmpRecord, tmpRecord[tmpSeed.IDField]);
					}
				}
			}
			catch (error)
			{
				// Seeding is an optimization; a miss just means the old per-row reads.
				this.pict.log.warn(`Error seeding the ${tmpSeed.Entity} entity cache: ${error.message || error}`);
			}
		}
	}

	onInteractiveLogin()
	{
		this.navInitialized = false;
		this.PermissionsControl.clear();
		this.pict.ContentAssignment.assignContent('#LIMS-Application-Container', `<div class="is-loading-splash has-background-text-soft" style="z-index: 25"><h1 class="title">Loading...</h1></div>`);
		this.loginAsync((pError) =>
		{
				this.pict.providers.PictRouter.router.navigate('/');
				if (pError)
				{
					this.pict.log.error(`Error during login: ${pError.message || pError}`);
					return;
				}
		});
	}

	onLoadDataAsync(fCallback)
	{
		super.onLoadDataAsync((pError) =>
		{
			if (!this.loggedIn)
			{
				return fCallback(pError);
			}
			this.pict.providers.QRCodeProvider.initializeLogos();
			// gather the shared bundle data (dropdown values, etc.)
			this.loadSharedBundle().then(() => this.onAfterLogin()).catch((error) =>
			{
				this.pict.log.error(`Error loading shared bundle: ${error.message || error}`);
			}).finally(() =>
			{
				fCallback(pError);
			});
		});
	}

	onAfterLoadDataAsync(fCallback)
	{
		return super.onAfterLoadDataAsync((pError) =>
		{
			//FIXME: Hack hiding the buttons because context is not being respected here... some sort of event life cycle thing again.
			//
			document.getElementById('HeadlightPanels-NavBar-Save-Button')?.classList?.add?.('is-hidden');
			document.getElementById('HeadlightPanels-NavBar-Solver-Button')?.classList?.remove?.('is-hidden');
			document.getElementById('HeadlightPanels-Navbar-Brand-Container')?.classList?.add?.('is-hidden');
			return fCallback(pError);
		});
	}

	async applySampleDisabledState()
	{
		const sampleCreateManifest = await this.pict.ManifestPackagingService.loadRecordManifestSchema('Sample', 'Create', this.pict.PictApplication.options.pict_configuration.Product);
		const sampleEditManifest = await this.pict.ManifestPackagingService.loadRecordManifestSchema('Sample', 'Edit', this.pict.PictApplication.options.pict_configuration.Product);
		if (sampleCreateManifest && sampleCreateManifest.Descriptors && !Object.keys(sampleCreateManifest.Descriptors).length)
		{
			this.AppData.disableSampleCreate = true;
			document.getElementById('lims-nav-create-sample')?.classList?.add?.('is-hidden');
		}
		if (sampleEditManifest && sampleEditManifest.Descriptors && !Object.keys(sampleEditManifest.Descriptors).length)
		{
			this.AppData.disableSampleEdit = true;
		}
	}


	//FIXME: I think this should get called more than it is - ex. if you are already logged in on page load...?
	async onAfterLogin()
	{
		this.PermissionsControl.clear();
		const session = this.AppData.CurrentUser;
		// Apply the session to the permissions library.
		this.PermissionsControl.setSession(session);
		const appAccess = await this.PermissionsControl.checkModulePermission('LIMS');
		if ('Denied' == appAccess)
		{
			this.pict.views.HeadlightPanels.toast({ message: `You do not have permission to access the LIMS app.`, type: 'is-danger' });
			this.logout();
			return;
		}
		// Prefill the manifest cache with the set of entities to cache.
		await this.pict.ManifestPackagingService.primeManifestCache(null, [ 'Sample', 'Location', 'LineItem', 'Test', 'Document', 'Project', 'TestInstance' ]);
		const promiseSet = [];
		promiseSet.push(this.pict.ManifestPackagingService.loadRecordSetSchemasForProduct(null, [ 'List', 'Filter', 'Dashboard' ]).then(()=>
		{
			if (this.navInitialized)
			{
				return;
			}
			this.navInitialized = true;
			const nav = this.pict.ManifestPackagingService.constructDashboardNav();
			if (!document.querySelector('#LIMSNavbarTopBurgerButton > .navbar-start').innerHTML.includes(nav.topNav))
			{
				document.querySelector('#LIMSNavbarTopBurgerButton > .navbar-start').innerHTML += nav.topNav;
			}
			const navArray = 
			[
				{
					Hash: 'Scan Sample',
					Name: 'Scan Sample',
					Icon: 'fas fa-qrcode',
					Address: null,
					// TODO: This method of hiding tabs is a bit jank, should refactor at some point.
					NavigationHandler: `_Pict.views.HeadlightPanels.handlePanelToggle('Samples', 'open', () =>
					{
						_Pict.views.HeadlightPanels.toggleTabContent('HeadlightPanels-SamplePanel-Scan', 'Sample');
						const stuffToHide = ['HeadlightPanels-SamplePanel-Create-Tab', 'HeadlightPanels-SamplePanel-Search-Tab'];
						for (let s of stuffToHide)
						{
							document.getElementById(s)?.classList?.add?.('is-hidden');
						}
					});`,
					PanelSource: null,
				},
				{
					Hash: 'Scanning Cart',
					Name: 'Scanning Cart',
					Icon: 'fas fa-scanner-gun',
					Address: null,
					NavigationHandler: '_Pict.views.QRPanel.open()',
					PanelSource: null,
				},
				{
					Hash: 'Samples',
					Name: 'Samples',
					Icon: 'fas fa-flask',
					Address: '#/PSRS/Sample/List/FilteredTo/FSF~CreateDate~DESC~0',
					PanelSource: null,
				},
				{
					Hash: 'Documents',
					Name: 'Documents',
					Icon: 'fas fa-files',
					Address: '#/PSRS/Document/List',
					PanelSource: null,
				},
				{
					Hash: 'Locations',
					Name: 'Locations',
					Icon: 'fas fa-location-dot',
					Address: '#/PSRS/Location/List',
					PanelSource: null,
				},
				{
					Hash: 'LIMSTestbed',
					Name: 'LIMS Testbed',
					Icon: 'fas fa-vial',
					Address: '#/Testbed',
					PanelSource: null,
				}
			].concat(nav.sideNav).concat([
				{
					Hash: 'Settings',
					Name: 'Settings',
					Icon: 'fas fa-cog',
					Address: null,
					PanelSource: 'settings',
				},
			]);

			this.pict.views.HeadlightPanels.setPanelConfiguration({
				PanelOptions: {
					Context: 'LIMS',
				},
				CustomNavigationList: navArray,
			});
		}));
		const summaryManifestEntities = [ 'LineItem', 'Test', 'Document', 'Project', 'TestInstance' ];
		promiseSet.push(this.pict.ManifestPackagingService.loadQuickSummaryManifests(summaryManifestEntities, require('./views/defaultSummaryManifests.json')));
		const printableManifestEntities = [ 'Sample', 'Location' ];
		promiseSet.push(this.pict.ManifestPackagingService.loadPrintableManifests(printableManifestEntities, require('./views/defaultPrintableManifests.json')));
		promiseSet.push(this.applySampleDisabledState());
		promiseSet.push(this.loadReportNamedInstances());
		promiseSet.push(this.loadSampleStatusVocabulary());
		await Promise.all(promiseSet);

		if (Array.isArray(this.options.DefaultRecordSetConfigurations))
		{
			this.pict.services.PictSectionRecordSet.loadRecordSetConfigurationArray(this.options.DefaultRecordSetConfigurations);
		}

		if (session?.LoggedIn)
		{
			this.pict.options.Timezone = session.Timezone || 'America/Los_Angeles'; //TODO: are there cases where we need to do project level customization here?
			//FIXME: this does not exist...
			//this.AppData.Module = await this.pict.providers.ModulesProvider.getModuleByHash('LIMS_MANAGEMENT');
		}
		this._updateUserProfile();

		this.pict.AppData.JumbotronShown = false;
		this.updateProgramDisplayInNav();

		const tmpAnticipate = this.pict.newAnticipate();
		tmpAnticipate.anticipate(
			(fNext) =>
			{
				//NOTE: reading defaultRoute here as navigating to '/' if we are already there doesn't trigger the router
				const currentPath = this.pict.providers.PictRouter.router.getCurrentLocation()?.url;
				if (!currentPath || currentPath === '/')
				{
					let tmpRoute = this.cachedLogoutRoute;
					if (!tmpRoute || tmpRoute == '/')
					{
						tmpRoute = this.pict.providers.LIMSRouter.defaultRoute;
					}
					this.cachedLogoutRoute = null;
					this.pict.providers.PictRouter.navigate(tmpRoute);
				}
				//TODO: can this wait until nav is done? should it?
				fNext();
			});

		return new Promise((resolve, reject) =>
		{
			tmpAnticipate.wait((pError) =>
			{
				if (pError)
				{
					return reject(pError);
				}
				resolve();
			});
		});
	}
}

module.exports = LIMS;

module.exports.default_configuration = (
	{
		Name: 'LIMS',
		Hash: 'LIMSPictRecordSetApplication',
		InitializeOnLoad: true,
		AutoRender: false,
		Document:
	{
		Required: false,
	},
	});

module.exports.default_configuration.pict_configuration = (
	{
		'Product': 'LIMS',
		'ProductVersion': '1.0.0',

		'PictApplicationConfiguration':
	{
		AutoRenderMainViewportViewAfterInitialize: false,
	},
		'IsHeadlightWebApplication': true,

		'Login':
	{
		Required: true,
	},

		'Manifests':
	{
		'Sample-List': require('./views/defaultSampleListManifest.json'),
		'Location-List': require('./views/defaultLocationListManifest.json'),
		'Document-List': require('./views/defaultDocumentListManifest.json'),
	},
		'DefaultRecordSetConfigurations':
	[
		{
			'RecordSet': 'Sample',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'Sample',

			'RecordSetURLPrefix': '/1.0/',
			'SearchFields': [ 'Name', 'QRCode', 'LabControlNumber', 'Notes' ],
			// Whole-row click opens the sample; quick-filter bar for the common cuts (name, when, who logged it).
			'RowClickOpensRecord': true,
			'QuickFilters': [ 'Name', { 'Field': 'CreateDate', 'Label': 'Logged' }, { 'Field': 'CreatingIDUser', 'Label': 'Logged by' } ],
			'RecordSetListColumns':
			[
				{ Key: 'LabControlNumber', DisplayName: 'PSID' },
				{ Key: 'Name', DisplayName: 'Name' },
				{ Key: 'ReceivedFrom', DisplayName: 'Source' },
			],
			'FilterEndpointOverride': 'LIMS/SamplesFiltered',
			'RecordSetListManifestOnly': true,
			// User-facing show/hide columns (manifest columns + remaining scalar schema columns).
			'RecordSetListColumnChooser': true,
			// Quick-bar toggle to enumerate soft-deleted rows (pairs with the audit columns).
			'RecordSetListShowDeletedFilter': true,
			'RecordSetListHasExtraColumns': false,
			//"RecordSetListExtraColumnsHeaderTemplate": '<th class="has-text-centered">PSID</th><th class="has-text-centered">Name</th><th class="has-text-centered">Source</th><th class="has-text-centered">Material</th><th class="has-text-centered">Source Location</th><th class="has-text-centered">Sampled On</th><th class="has-text-centered">Received On</th><th class="has-text-centered">Logged By</th>',
			//"RecordSetListExtraColumnRowTemplate": '<td>{~D:Record.Data.LabControlNumber~}</td><td>{~D:Record.Data.Name~}</td><td>{~D:Record.Data.ReceivedFrom~}</td><td>{~E:Material^Record.Data.IDMaterial^Entity-Name~}</td><td>{~ParseSourceLocation:Record.Data~}</td><td>{~DateFormatLIMS:Record.Data.DesignatedTime^MM/DD/YYYY~}</td><td>{~DateFormatLIMS:Record.Data.Extension.ReceivedDate^MM/DD/YYYY~}</td><td>{~E:User^Record.Data.CreatingIDUser^User-Name~}</td>',
			'RecordSetIgnoreFilterFields':
			[
				'IDSample',
				'GUIDSample',
				'Deleted',
				'DeleteDate',
				'DeletingIDUser',
				'ExternalSyncDate',
				'IDCustomer',
				'CaptureLatitude',
				'CaptureLongitude',
				'ParentIDSample',
				'IDProductionPlant',
				'IDLineItem',
				'Status',
				'ProductName',
				'MaterialType',
				'IDOrganization',
				'IDMixDesign',
				'ManufacturerCode',
				'IntendedUse',
				'BuyUSA',
				'ReceivedFrom',
				'GeographicalArea',
				'Station',
				'Offset',
				'RepresentedQuantity',
				'SampledQuantity',
				'SampledQuantityUnits',
				'RepresentedQuantityUnits',
				'Units',
				'RequestedBy',
				'ExternalSyncGUID',
				'IDProduct',
				'SampledByIDUser',
				'WitnessIDUser',
				'AuthorizedIDUser',
				'CMSIDUser',
				'Datum',
			],
			'RecordSetListDefaultManifest': 'Sample-List',
			'RecordSetListManifests': [ 'Sample-List' ],
			'RecordSetDashboardDefaultManifest': 'Sample-List',
			'RecordSetDashboardManifests': [ 'Sample-List' ],
		},
		{
			'RecordSet': 'Location',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'Location',

			'RecordSetListColumns': [
				{
					'Key': 'Name',
					'DisplayName': 'Name',
				},
				{
					'Key': 'Type',
					'DisplayName': 'Type',
				},
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListManifests': [ 'Location-List' ],
			'RecordSetDashboardDefaultManifest': 'Location-List',
			'RecordSetDashboardManifests': [ 'Location-List' ],
			// User-facing show/hide columns (manifest columns + remaining scalar schema columns).
			'RecordSetListColumnChooser': true,
			// Quick-bar toggle to enumerate soft-deleted rows (pairs with the audit columns).
			'RecordSetListShowDeletedFilter': true,
			'RecordSetListHasExtraColumns': true,
			'SearchFields': [ 'Name', 'Type' ],
			'RowClickOpensRecord': true,
			'QuickFilters': [ 'Name', { 'Field': 'CreateDate', 'Label': 'Added' } ],

			'RecordSetURLPrefix': '/1.0/',
			'RecordSetIgnoreFilterFields':
			[
				'CreatingIDUser', 'UpdatingIDUser', 'Deleted', 'DeleteDate', 'DeletingIDUser', 'ExternalSyncDate', 'Type', 'Description', 'Address', 'ExternalLocationID', 'ParentIDLocation', 'Phone', 'Latitude', 'Longitude', 'IDCustomer',
			],
		},
		{
			'RecordSet': 'Document',

			'RecordSetType': 'MeadowEndpoint',
			'RecordSetMeadowEntity': 'Document',

			'RecordSetListColumns': [
				{
					'Key': 'Name',
					'DisplayName': 'Name',
				},
				{
					'Key': 'Status',
					'DisplayName': 'Status',
				},
			],
			'RecordSetListManifestOnly': true,
			'RecordSetListManifests': [ 'Document-List' ],
			'RecordSetListLiteFetch': true,
			// User-facing show/hide columns; under Lite, showing a schema column refetches once
			// with the projection widened to include it.
			'RecordSetListColumnChooser': true,
			// Quick-bar toggle to enumerate soft-deleted rows (pairs with the audit columns).
			'RecordSetListShowDeletedFilter': true,
			'RecordSetDashboardDefaultManifest': 'Document-List',
			'RecordSetDashboardManifests': [ 'Document-List' ],
			'RecordSetListHasExtraColumns': false,
			'SearchFields': [ 'Name' ],
			'RowClickOpensRecord': true,
			'QuickFilters': [ 'Name', 'Status', { 'Field': 'CreateDate', 'Label': 'Created' } ],
			//"RecordSetListExtraColumnsHeaderTemplate": '<th class="has-text-centered">Created By</th><th class="has-text-centered">Created On</th><th class="has-text-centered">Type</th><th class="has-text-centered">Project</th>',
			//"RecordSetListExtraColumnRowTemplate": '<td>{~E:User^Record.Data.CreatingIDUser^User-Name~}</td><td>{~DateFormatLIMS:Record.Data.CreateDate^MM/DD/YYYY~}</td><td>{~DocumentTypeName:Record.Data.DocumentType~}</td><td>{~E:Project^Record.Data.IDProject^Entity-Name~}</td>',
			'RecordSetURLPrefix': '/1.0/',
			'RecordSetIgnoreFilterFields':
			[
				'CreatingIDUser',
				'UpdatingIDUser',
				'Description',
				'Deleted',
				'DeleteDate',
				'DeletingIDUser',
				'IDDevice',
				'IDProject',
				'DocumentType',
				'ExternalSyncDate',
				'Version',
				'AppHash',
				'Timezone',
				'Shift',
				'StateName',
				'StateStep',
				'Locked',
				'Form',
				'FormDataJSON',
				'EntityDataJSON',
				'ExternalSyncGUID',
				'IDCustomer',
				'StaleUserActions',
			],
		},
	],
	});
